import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Eye, EyeOff, KeyRound, Loader2, Lock, ShieldCheck, X } from "lucide-react";
import { changePassword, resetPasswordStatus } from "../../redux/profileSlice";
import {
  passwordStrengthLevel,
  passwordStrengthScore,
  validateNewPassword,
} from "../../utils/passwordHelpers";

const PasswordField = ({
  id,
  label,
  value,
  onChange,
  visible,
  onToggleVisible,
  error,
  autoComplete,
}) => (
  <div className="prof-pw-field">
    <label className="prof-pw-label" htmlFor={id}>
      {label}
    </label>
    <div className={`prof-pw-input-wrap${error ? " prof-pw-input-wrap-error" : ""}`}>
      <Lock size={13} aria-hidden />
      <input
        id={id}
        type={visible ? "text" : "password"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        className="prof-pw-eye"
        onClick={onToggleVisible}
        aria-label={visible ? "Hide password" : "Show password"}
      >
        {visible ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
    {error ? <p className="prof-pw-error">{error}</p> : null}
  </div>
);

const PasswordStrength = ({ password }) => {
  const score = passwordStrengthScore(password);
  const level = passwordStrengthLevel(password);

  return (
    <div className="prof-pw-strength">
      <div className="prof-pw-strength-bar" aria-hidden>
        <span
          className={`prof-pw-strength-fill prof-pw-strength-${level.tone}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <p className={`prof-pw-strength-label prof-pw-strength-${level.tone}`}>
        {password ? level.label : "Enter a password"}
      </p>
    </div>
  );
};

const ChangePasswordModal = ({ open, onClose, userId, onNotify }) => {
  const dispatch = useDispatch();
  const titleId = useId();
  const passwordStatus = useSelector((s) => s.profile.passwordStatus);
  const saving = passwordStatus === "loading";

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
    setErrors({});
    dispatch(resetPasswordStatus());
  };

  useEffect(() => {
    if (!open) return undefined;

    resetForm();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = {};
    const passwordErrors = validateNewPassword(newPassword);

    if (!oldPassword.trim()) nextErrors.oldPassword = "Current password is required";
    if (passwordErrors.length > 0) nextErrors.newPassword = passwordErrors.join(", ");
    if (newPassword !== confirmPassword) nextErrors.confirmPassword = "Passwords do not match";

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    if (!userId) {
      onNotify?.("error", "Update failed", "Sign in again to change your password.");
      return;
    }

    const result = await dispatch(
      changePassword({ id: userId, oldPassword, newPassword })
    );

    if (changePassword.fulfilled.match(result)) {
      onNotify?.(
        "success",
        "Password updated",
        result.payload?.message || "Your password was changed successfully."
      );
      handleClose();
      return;
    }

    onNotify?.("error", "Update failed", String(result.payload || "Try again."));
  };

  return createPortal(
    <div className="prof-pw-root" role="presentation">
      <button
        type="button"
        className="prof-pw-backdrop"
        aria-label="Close dialog"
        disabled={saving}
        onClick={handleClose}
      />

      <form
        className="prof-pw-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="prof-pw-head">
          <span className="prof-pw-head-icon" aria-hidden>
            <KeyRound size={14} />
          </span>
          <div className="prof-pw-head-copy">
            <p className="prof-pw-eyebrow">Security</p>
            <h2 id={titleId}>Change password</h2>
          </div>
          <button
            type="button"
            className="prof-pw-close"
            aria-label="Close"
            disabled={saving}
            onClick={handleClose}
          >
            <X size={14} />
          </button>
        </header>

        <div className="prof-pw-body">
          <PasswordField
            id="prof-pw-old"
            label="Current password"
            value={oldPassword}
            onChange={(value) => {
              setOldPassword(value);
              setErrors((prev) => ({ ...prev, oldPassword: "" }));
            }}
            visible={showOld}
            onToggleVisible={() => setShowOld((visible) => !visible)}
            error={errors.oldPassword}
            autoComplete="current-password"
          />

          <PasswordField
            id="prof-pw-new"
            label="New password"
            value={newPassword}
            onChange={(value) => {
              setNewPassword(value);
              setErrors((prev) => ({ ...prev, newPassword: "" }));
            }}
            visible={showNew}
            onToggleVisible={() => setShowNew((visible) => !visible)}
            error={errors.newPassword}
            autoComplete="new-password"
          />

          <PasswordStrength password={newPassword} />

          <PasswordField
            id="prof-pw-confirm"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(value) => {
              setConfirmPassword(value);
              setErrors((prev) => ({ ...prev, confirmPassword: "" }));
            }}
            visible={showConfirm}
            onToggleVisible={() => setShowConfirm((visible) => !visible)}
            error={errors.confirmPassword}
            autoComplete="new-password"
          />
        </div>

        <footer className="prof-pw-actions">
          <button
            type="button"
            className="prof-pw-btn prof-pw-btn-ghost"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="prof-pw-btn prof-pw-btn-primary" disabled={saving}>
            {saving ? (
              <Loader2 size={13} className="animate-spin" aria-hidden />
            ) : (
              <ShieldCheck size={13} aria-hidden />
            )}
            Update password
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
};

export default ChangePasswordModal;
